<template>
  <v-container class="px-6 px-sm-12">
    <PageHeader
      :header-text="'Per Day Voyage Report'"
      :header-icon="'mdi-calendar'"
    />
    <!-- Date Picker -->
    <div class="d-flex justify-space-between">
      <DatePicker
        :currentDate="date"
        :maxDate="dateNow"
        v-on:dateChanged="dateChanged"
      />
      <ExcelDownloadButton
        :size="headerButtonSize"
        :onClick="exportAsExcel"
        :isLoading="isExportingExcel"
      />
    </div>
    <div v-if="loading">
      <v-progress-circular color="primary" indeterminate> </v-progress-circular>
    </div>
    <div v-else>
      <!-- High Level Details -->
      <DailyReportSummary
        class="my-4"
        :dailyReport="dailyReport"
        :values="summaryValues"
      />
      <!-- Voyages -->
      <v-card class="mb-4">
        <DailyVoyageReportVoyageTable
          :dailyReportVoyages="dailyReport.voyages"
        />
      </v-card>
      <!-- Activities -->
      <v-card class="mb-4">
        <DailyVoyageReportActivityTable :dailyActivities="activities" />
      </v-card>
      <!-- Bulk Transactions -->
      <v-card class="mb-4">
        <DailyVoyageReportBulkTransactionTable :dailyBulkTransactions="dailyReport.bulkTransactions"
        />
      </v-card>
      <!-- Safety Observations -->
      <v-card class="mb-4" v-if="isSafetyObservationSiteEnabled">
        <DailyVoyageReportSafetyObservationTable :dailySafetyObservations="dailyReport.safetyObservations" />
      </v-card>
    </div>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';
import PageHeader from '../components/ui/PageHeader.vue';
import DailyReportSummary from '../components/daily-reports/DailyReportSummary.vue';
import DailyVoyageReportVoyageTable from '../components/daily-reports/DailyVoyageReportVoyageTable.vue';
import DailyVoyageReportActivityTable from '../components/daily-reports/DailyVoyageReportActivityTable.vue';
import DailyVoyageReportBulkTransactionTable from '../components/daily-reports/DailyVoyageReportBulkTransactionTable.vue';
import DailyVoyageReportSafetyObservationTable from '../components/daily-reports/DailyVoyageReportSafetyObservationTable.vue';
import ExcelDownloadButton from '../components/ui/ExcelDownloadButton.vue';
import DownloadHelperMixin from '../mixins/DownloadHelperMixin';
import DatePicker from '../components/ui/DatePicker.vue';

export default {
  name: 'PerDayVoyageReport',
  components: {
    PageHeader,
    DailyReportSummary,
    DailyVoyageReportVoyageTable,
    DailyVoyageReportActivityTable,
    DailyVoyageReportBulkTransactionTable,
    DailyVoyageReportSafetyObservationTable,
    ExcelDownloadButton,
    DatePicker,
  },
  mixins: [ResponsiveSizeHelperMixin, DownloadHelperMixin],
  data() {
    return {
      loading: false,
      isExportingExcel: false,
      date: null,
      dateNow: new Date().toISOString().substr(0, 10),
    };
  },
  computed: {
    ...mapGetters({
      getActiveSite: 'userStore/getActiveSite',
      getActiveSiteConfig: 'userStore/getActiveSiteConfig',
      getDailyVoyageReport: 'dailyReportsStore/getDailyVoyageReport',
    }),
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) return null;
      return site.id;
    },
    dailyReport() {
      return this.getDailyVoyageReport;
    },
    activities() {
      return this.dailyReport.activities.map((x) => (
        {
          ...x,
          vesselName: x.voyageReport.vesselName,
          voyageNo: x.voyageReport.voyageNo,
        }
      ));
    },
    bulkTransactions() {
      return this.dailyReport.bulkTransactions.map((x) => (
        {
          ...x,
          vesselName: x.vesselActivity.voyageReport.vesselName,
          voyageNo: x.vesselActivity.voyageReport.voyageNo,
        }
      ));
    },
    safetyObservations() {
      return this.dailyReport.safetyObservations.map(so => (
        {
          ...so,
          vesselName: so.voyageReport.vesselName,
          voyageNo: so.voyageReport.voyageNo,
        }
      ));
    },
    isSafetyObservationSiteEnabled() {
      return this.getActiveSiteConfig && this.getActiveSiteConfig.safetyObservationsEnabled;
    },
    summaryValues() {
      const valuesArray = [
        {
          value: this.dailyReport.voyageCountForDay, prevDayValue: this.dailyReport.voyageCountForPreviousDay, label: 'Voyages', icon: 'mdi-ferry',
        },
        {
          value: this.dailyReport.activityCountForDay, prevDayValue: this.dailyReport.activityCountForPreviousDay, label: 'Activities', icon: 'mdi-timer',
        },
        {
          value: this.dailyReport.bulkTransactionCountForDay, prevDayValue: this.dailyReport.bulkTransactionCountForPreviousDay, label: 'Bulk Transactions', icon: 'mdi-handshake',
        }
      ];

      if (this.isSafetyObservationSiteEnabled) {
        valuesArray.push({
          value: this.dailyReport.safetyObservationCountForDay, prevDayValue: this.dailyReport.safetyObservationCountForPreviousDay, label: 'Safety Observations', icon: 'mdi-comment-alert',
        });
      }

      return valuesArray;
    },
  },
  watch: {
    activeSiteId(newVal) {
      this.loadDailyVoyageReport(this.date);
    },
  },
  created() {
    this.date = this.dateNow;

    const { date } = this.$route.params;
    if (date) this.date = date;

    this.loadDailyVoyageReport(this.date);
  },
  methods: {
    loadDailyVoyageReport(date) {
      this.loading = true;
      this.$store.dispatch('dailyReportsStore/loadDailyVoyageReport', { activeSiteId: this.activeSiteId, date })
        .finally(() => {
          this.loading = false;
        });
    },
    dateChanged(newDate) {
      this.date = newDate;
      this.loadDailyVoyageReport(newDate);
    },
    exportAsExcel() {
      this.isExportingExcel = true;
      this.downloadFile('dailyReportsStore/getDailyVoyageReportAsExcelFile', { activeSiteId: this.activeSiteId, date: this.date })
        .finally(() => { this.isExportingExcel = false; });
    },
  },
};
</script>
